<template>
  <Modal sheetbelowsm close-button title="User Data Management" :secondary-button="$t('cancel')" :primary-button-loading="loading" :loading="fetching" @secondary="cancel" @close="cancel">
    <div class="sm:w-screen90 md:w-900 min-h-500 flex py-7 px-10 flex-wrap">
      <div v-if="!fetching" class="w-full">
        <div class="relative font-600 text-16">{{ $t('navigation.data_adapters') }}</div>
        <div v-if="!this.hasDataSources" class="mt-3">No data adapters available for the project</div>
        <CollapseCard v-for="dataAdapter in projectActivatedDataAdapters" :key="dataAdapter.adapter_type" :value="false" :showIcon="true" :scroll="false" class="shadow-card mb-4">
          <template #header>
            <div class="flex justify-between w-full flex-wrap -mt-3">
              <div class="flex items-center mt-3">
                <img :src="dataAdapter.logo" class="w-8 h-8 mr-2" :alt="dataAdapter.name" />
                <div class="flex items-center flex-wrap">
                  <span class="font-600 mr-2">{{ dataAdapter.name }}</span>
                </div>
              </div>
            </div>
          </template>
          <template #content>
            <div v-if="hasMultipleAccounts(dataAdapter.adapter_type)">
              <div class="px-4 pb-4">
                <div class="pt-3 px-3">{{ $t('datastore.no_account_selected') }}</div>
              </div>
            </div>
            <div v-else class="pb-5">
              <DataAdapterConfiguration
                for-user
                :type="dataAdapter.adapter_type"
                v-model:default-config="config[dataAdapter.adapter_type]"
                :ref="`configuration-${dataAdapter.adapter_type}`"
                class="px-5"
                @update:config="onConfigChange($event, dataAdapter.adapter_type)"
              />
              <div class="flex w-full justify-between">
                <PillButton error :text="$t('delete')" class="mt-4 ml-5" @click="onDeleteAdapterClick(dataAdapter.adapter_type)"></PillButton>
                <PillButton
                  primary
                  :loading="!!updatingDataAdapter"
                  :disabled="!!updatingDataAdapter"
                  :text="$t('save')"
                  class="mt-4 mr-5"
                  @click="updateAdapter(dataAdapter.adapter_type)"
                ></PillButton>
              </div>
            </div>
          </template>
        </CollapseCard>
        <div class="relative font-600 text-16 mt-5">Features</div>
        <div v-if="!this.hasFeatures" class="mt-3">User has no feature data available</div>
        <CollapseCard v-for="feature in availableReports" :key="feature.label" :value="false" :showIcon="true" :scroll="false" class="shadow-card my-4">
          <template #header>
            <div class="flex justify-between w-full flex-wrap -mt-3">
              <div class="flex items-center mt-3">
                <Icon :name="feature.icon" class="w-6 h-6 text-primary mr-3" />
                <div class="flex items-center flex-wrap">
                  <span class="font-600 mr-2">{{ feature.title }}</span>
                </div>
              </div>
            </div>
          </template>
          <template #content>
            <div class="pb-5">
              <div class="flex w-full justify-between">
                <IconButton icon="play_circle" v-tooltip="'Start'" v-if="feature.status === 'Stopped'" class="sm:ml-1 text-primary py-0" @click.stop.prevent="onStartClicked(feature)"></IconButton>
                <IconButton icon="stop_circle" v-tooltip="'Stop'" v-if="feature.status === 'Running'" class="sm:ml-1 text-primary py-0" @click.stop.prevent="onStopClicked(feature)"></IconButton>
                <Badge class="w-16 h-5" small v-if="!feature.status || feature.status === 'Created'" info text="Inactive"></Badge>
                <Badge class="w-16 h-5" small v-if="feature.status === 'Running'" success text="Active"></Badge>
                <Badge class="w-16 h-5" small v-if="feature.status === 'Stopped'" error text="Stopped"></Badge>
              </div>
            </div>
          </template>
        </CollapseCard>
      </div>
    </div>
  </Modal>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { DATA_ADAPTER_ICONS, DATA_ADAPTERS } from '@/constants';
import DataAdapterConfiguration from '../../../../data-adapter/DataAdapterConfiguration.vue';
import { apiPostProjectUserDataAdapter, apiDelProjectUserDataAdapter } from '../../../../../helpers/api';
import DeleteModal from '../../../../modals/DeleteModal.vue';

export default {
  name: 'EditUserDataModal',
  components: { DataAdapterConfiguration },
  props: {
    email: String,
    name: String,
  },
  data() {
    return {
      loading: false,
      fetching: false,
      config: {},
      tenantAdapters: {},
      dirty: {},
      updatingDataAdapter: null,
      features: [],
      projectFeatures: {},
    };
  },
  emits: ['add', 'close'],
  computed: {
    DATA_ADAPTERS() {
      return DATA_ADAPTERS;
    },
    ...mapState(['availableDataAdapters']),
    ...mapGetters(['getProjectDataAdapters', 'getProjectFeatures']),
    projectActivatedDataAdapters() {
      const projectActivatedDataAdapters = Object.values(this.getProjectDataAdapters()).filter((projectAdapter) => projectAdapter.config.enabled);
      return projectActivatedDataAdapters.map((item) => {
        return {
          ...item,
          name: this.availableDataAdapters[item.adapter_type].name,
          logo: DATA_ADAPTER_ICONS[item.adapter_type],
        };
      });
    },
    projectId() {
      return this.$route.params.projectId;
    },
    hasDataSources() {
      return Object.keys(this.getProjectDataAdapters()).length > 0;
    },
    hasFeatures() {
      return this.features.length > 0;
    },
    availableReports() {
      const features = [];
      this.features.forEach((f) => {
        if (f.feature === 'deep_research_reports/ORGANISATION_STRATEGY_REPORT') {
          const feature = this.projectFeatures?.find?.((item) => item.value === 'ORGANISATION_STRATEGY_REPORT');
          features.push({ ...feature, notification_frequency: f?.notification_frequency, status: f?.status });
        } else if (f.feature === 'deep_research_reports/ORGANISATION_OPPORTUNITY_REPORT') {
          const feature = this.projectFeatures?.find?.((item) => item.value === 'ORGANISATION_OPPORTUNITY_REPORT');
          features.push({ ...feature, notification_frequency: f?.notification_frequency, status: f?.status });
        }
      });
      return features;
    },
  },
  async created() {
    this.fetching = true;
    await this.fetchData();
    this.fetching = false;
  },
  methods: {
    ...mapActions(['startDeepResearchReport', 'stopDeepResearchReport', 'fetchProjectUserFeatures', 'fetchProjectFeatures', 'updateProjectUser', 'showToastMessage', 'fetchProjectUserDataAdapters']),
    async fetchData() {
      this.config = {};
      await this.fetchProjectFeatures({ project_id: this.projectId });
      let response = await this.fetchProjectUserDataAdapters({ project_id: this.projectId, email: this.email });
      this.tenantAdapters = response.user_adapters;
      Object.keys(this.getProjectDataAdapters()).forEach((key) => {
        this.config[key] = structuredClone(this.getProjectDataAdapters()[key].config);
      });
      Object.keys(this.tenantAdapters).forEach((key) => {
        Object.keys(this.tenantAdapters[key]).forEach((configKey) => {
          if (key in this.config) {
            this.config[key][configKey] = this.tenantAdapters[key][configKey];
          }
        });
      });
      response = await this.fetchProjectUserFeatures({ project_id: this.projectId, email: this.email });
      this.features = response;
      const projectFeatures = this.getProjectFeatures();
      this.projectFeatures = [];
      Object.keys(projectFeatures).forEach((f) => {
        if (f === 'deep_research_reports' && projectFeatures[f].enabled) {
          this.projectFeatures = projectFeatures[f].config.reports;
        }
      });
    },
    hasMultipleAccounts(adapterType) {
      return this.availableDataAdapters[adapterType].accounts;
    },
    onConfigChange(config, adapterType) {
      this.dirty[adapterType] = true;
    },
    onDeleteAdapterClick(adapterType) {
      this.$showModal(DeleteModal, {
        subtitle: this.$t('projects.data_adapters.remove_user_data_adapter', { adapter: adapterType }),
        onConfirm: async () => {
          await this.deleteAdapter(adapterType);
          await this.fetchData();
        },
      });
    },
    async deleteAdapter(adapterType) {
      try {
        const response = await apiDelProjectUserDataAdapter({ project_id: this.projectId, email: this.email, adapter_type: adapterType });

        if (response.status === 200) {
          this.showToastMessage({ message: this.$t('projects.data_adapters.removed_user_adapter_successfully'), type: 'success' });
        } else {
          this.showToastMessage({ title: response.data.message || this.$t('projects.data_adapters.failed_to_delete_user_adapter'), type: 'error' });
        }
      } catch {
        this.showToastMessage({ title: this.$t('projects.data_adapters.failed_to_delete_user_adapter'), type: 'error' });
      }
    },
    async updateAdapter(adapterType) {
      const valid = await this.$refs[`configuration-${adapterType}`][0].validate();
      if (valid.valid) {
        try {
          this.updatingDataAdapter = adapterType;
          const response = await apiPostProjectUserDataAdapter({ project_id: this.projectId, adapter_type: adapterType, email: this.email, config: this.config[adapterType] });
          this.updatingDataAdapter = null;
          if (response.status === 200) {
            this.showToastMessage({ message: this.$t('projects.data_adapters.updated_user_adapter_successfully'), type: 'success' });
          } else {
            this.showToastMessage({ title: response.data.message || this.$t('projects.data_adapters.failed_to_update_user_adapter'), type: 'error' });
          }
        } catch {
          this.showToastMessage({ title: this.$t('projects.data_adapters.failed_to_update_user_adapter'), type: 'error' });
        }
      }
    },
    async save() {
      const valid = await this.$refs.validationObserver.validate();

      if (valid.valid) {
        this.loading = true;

        // const { email, name, timezone, country, organisation, position, language, flags } = this;
        // const response = await this.updateProjectUser({ project_id: this.projectId, email, name, timezone, country, organisation, position, language, flags });

        // if (response) {
        //   this.showToastMessage({ message: this.$t('projects.iam.added_successfully'), type: 'success' });
        //   this.$emit('add');
        //   this.$emit('close');
        //   this.loading = false;
        //   this.cancel();
        // } else {
        //   this.loading = false;
        // }
      } else {
        this.loading = false;
      }
    },
    cancel() {
      this.$emit('close');
    },
    async onStartClicked(report) {
      const success = await this.startDeepResearchReport({
        project_id: this.projectId,
        report: report.value,
        notification_frequency: report.notification_frequency,
        email: this.email,
        name: this.name,
      });
      if (success) {
        const response = await this.fetchProjectUserFeatures({ project_id: this.projectId, email: this.email });
        this.features = response;
      }
    },
    async onStopClicked(report) {
      const success = await this.stopDeepResearchReport({ project_id: this.projectId, report: report.value, email: this.email });
      if (success) {
        const response = await this.fetchProjectUserFeatures({ project_id: this.projectId, email: this.email });
        this.features = response;
      }
    },
  },
};
</script>
